import { LeftOutlined, PlusOutlined } from '@ant-design/icons'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ZodError } from 'zod'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import AtomSearchInput from '@/components/Atoms/AtomSearchInput/AtomSearchInput'
import Loader from '@/components/Atoms/Loader/Loader'
import BrandCreationModal from '@/components/EntityCreationModals/BrandCreationModal/BrandCreationModal'
import TreeTiles from '@/components/TreeTiles/TreeTiles'
import { GENERAL_ERRORS_MESSAGES } from '@/constants'
import Tile from '@/pages/CategoriesByBrandsCatalog/Tile/Tile'
import { categorySchema } from '@/schemas'
import { useCategoriesByBrandsCatalogStore } from '@/stores/feed/categoriesByBrandsCatalogStore'
import { useTreeTilesStore } from '@/stores/feed/treeTilesStore'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import { BrandInterface, CategoryInterface } from '@/types'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { fetchBrands } from '@/utils/api/fetch/fetchEntities'
import { hasRoleFromGroup } from '@/utils/auth/roleGroups'
import { filterApprovedCategoryTree } from '@/utils/catalog/approvalStatus'
import { cleanCategoryTree } from '@/utils/catalog/cleanDescription'

import cn from './CategoriesByBrandsCatalog.module.scss'

function CategoriesByBrandsCatalog() {
  const [brands, setBrands] = useState<BrandInterface[]>([])
  const [brandsLoading, setBrandsLoading] = useState<boolean>(false)
  const [categoriesTreeByBrand, setCategoriesTreeByBrand] = useState<
    CategoryInterface[]
  >([])
  const [openBrandModal, setOpenBrandModal] = useState<boolean>(false)

  const [categoriesTreeByBrandLoading, setCategoriesTreeByBrandLoading] =
    useState<boolean>(false)
  const [
    categoriesTreeByBrandLoadingError,
    setCategoriesTreeByBrandLoadingError,
  ] = useState<string>('')
  const [fetchingError, setFetchingError] = useState<string>('')

  const parentRef = useRef<HTMLDivElement>(null)

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const { currentBrand, setCurrentBrand } = useCategoriesByBrandsCatalogStore(
    (state) => state
  )
  const brandSearch = useCategoriesByBrandsCatalogStore(
    (state) => state.brandSearch
  )
  const setBrandSearch = useCategoriesByBrandsCatalogStore(
    (state) => state.setBrandSearch
  )
  const normalizedBrandSearch = brandSearch.trim().toLowerCase()
  const brandsBySearch = normalizedBrandSearch
    ? brands.filter((brand) =>
        brand.description.toLowerCase().includes(normalizedBrandSearch)
      )
    : brands

  const { categoriesPrioritiesVersion, brandsPrioritiesVersion } =
    useTreeTilesStore((state) => state)

  const { currentUser, selectedOrganisation } = useUserInfoStore(
    (state) => state
  )
  const canCreateBrand = useUserInfoStore((state) =>
    hasRoleFromGroup(state.tokenRoles, 'CATALOG_EDITORS')
  )

  const isOrganisationSelected =
    Object.keys(selectedOrganisation || {}).length > 0

  useEffect(() => {
    const brandIdFromUrl = searchParams.get('brandId')

    if (brandIdFromUrl) {
      if (currentBrand.id !== brandIdFromUrl) {
        const foundBrand = brands.find((b) => b.id === brandIdFromUrl)
        if (foundBrand) {
          setCategoriesTreeByBrand([])
          setCurrentBrand(foundBrand)
        }
      }
    } else {
      if (currentBrand.id) {
        setCurrentBrand({} as BrandInterface)
      }
    }
  }, [searchParams, brands, currentBrand.id, setCurrentBrand])

  useEffect(() => {
    return () => {
      setCurrentBrand({} as BrandInterface)
    }
  }, [setCurrentBrand])

  const GAP = 32
  const TILE_VERTICAL_PADDING = 16 * 2

  function getColumnCount() {
    const w = window.innerWidth
    if (w < 480) return 1
    if (w < 768) return 2
    if (w < 1200) return 3
    if (w < 1440) return 4
    if (w < 1850) return 5
    return 6
  }

  const columnCount = getColumnCount()
  const rowCount = Math.ceil(brandsBySearch.length / columnCount)

  const estimateRowHeight = () => {
    const width = window.innerWidth || (parentRef.current?.offsetWidth ?? 1200)
    const tileWidth = (width - GAP * (columnCount - 1)) / columnCount

    const expectedTileHeight = tileWidth * 0.85
    return Math.round(expectedTileHeight + TILE_VERTICAL_PADDING + GAP)
  }

  const estimateSize = useCallback(() => estimateRowHeight(), [columnCount])

  const rowVirtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize,
    overscan: 10,
  })

  useEffect(() => {
    const handler = () => rowVirtualizer.measure()
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [rowVirtualizer])

  useEffect(() => {
    rowVirtualizer.measure()
  }, [brandsBySearch.length, rowVirtualizer])

  useEffect(() => {
    if (!currentBrand?.id) {
      setCategoriesTreeByBrand([])
      return
    }

    setCategoriesTreeByBrandLoading(true)
    setCategoriesTreeByBrandLoadingError('')

    api
      .get(`catalog/brands/${currentBrand.id}`)
      .then((response) => {
        const catalogData = response.data.brandCatalog || response.data
        const treeData = catalogData.categoryTree || []

        const validatedCategories = categorySchema.array().parse(treeData)
        const cleanedCategories = cleanCategoryTree(
          filterApprovedCategoryTree(validatedCategories)
        )

        setCategoriesTreeByBrand(cleanedCategories)
      })
      .catch((error) => {
        if (error instanceof AxiosError) {
          setCategoriesTreeByBrandLoadingError(getGeneralErrorMessage(error))
        } else {
          setCategoriesTreeByBrandLoadingError(
            GENERAL_ERRORS_MESSAGES.ZOD_ERROR
          )
        }
      })
      .finally(() => setCategoriesTreeByBrandLoading(false))
  }, [currentBrand.id, categoriesPrioritiesVersion])

  const getBrands = useCallback(async () => {
    try {
      setBrandsLoading(true)
      const brandsFetched = await fetchBrands()

      setBrands(brandsFetched)
    } catch (error) {
      if (error instanceof AxiosError) {
        setFetchingError(getGeneralErrorMessage(error))
      }
      if (error instanceof ZodError) {
        setFetchingError(GENERAL_ERRORS_MESSAGES.ZOD_ERROR)
      }
    } finally {
      setBrandsLoading(false)
    }
  }, [])

  useEffect(() => {
    getBrands()
  }, [getBrands, brandsPrioritiesVersion])

  const handleClickBrand = (brand: BrandInterface) => {
    if (currentUser?.organisationShortModel?.isLegal === false) {
      toast.warning('Организация не активирована', {
        description: 'Пожалуйста, обратитесь к своему менеджеру для активации',
      })
      return
    }

    const newParams = new URLSearchParams(searchParams)
    newParams.set('brandId', brand.id)
    newParams.delete('categoryId')
    newParams.delete('groupId')

    navigate({ search: newParams.toString() }, { state: { scrollToTop: true } })
  }

  const handleBackToBrands = () => {
    navigate({ search: '' }, { replace: true })
  }

  // useEffect(() => {
  //   window.scrollTo({ top: 0, behavior: 'instant' })
  // }, [currentBrand.id])

  return (
    <section className={cn.categoriesByBrandsCatalog}>
      <BrandCreationModal
        open={openBrandModal}
        setOpen={setOpenBrandModal}
        setBrands={() => getBrands()}
      />

      <div className={cn.categoriesByBrandsCatalogHeader}>
        {!brandsLoading &&
          currentUser?.organisationShortModel?.isLegal === false && (
            <div
              className={
                isOrganisationSelected
                  ? `${cn.warningBanner} ${cn.organisationSelected}`
                  : cn.warningBanner
              }
            >
              ⚠️ Организация не активирована
            </div>
          )}
      </div>
      {Object.keys(currentBrand).length > 0 && (
        <div className={cn.currentBrandInfoWithBackToBrandButton}>
          <div className={cn.backToBrandButton} onClick={handleBackToBrands}>
            <div className={cn.leftOutlinedIconContainer}>
              <LeftOutlined />
            </div>
            <span>Все бренды</span>
          </div>
          <div className={cn.currentBrandInfo}>
            <span>Текущий бренд:</span>
            <span>{currentBrand.description}</span>
          </div>
        </div>
      )}
      {!Object.keys(currentBrand).length && !brandsLoading && (
        <div className={cn.searchWithButton}>
          <AtomSearchInput
            className={cn.search}
            placeholder="Название бренда"
            aria-label="Поиск бренда"
            value={brandSearch}
            onChange={(event) => setBrandSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') event.currentTarget.blur()
            }}
            isOrganisationSelected={isOrganisationSelected}
          />
          {canCreateBrand && (
            <AtomButton
              icon={<PlusOutlined />}
              onClick={() => setOpenBrandModal(true)}
            >
              Добавить бренд
            </AtomButton>
          )}
        </div>
      )}
      {brandsBySearch.length > 0 &&
        !fetchingError &&
        !brandsLoading &&
        !Object.keys(currentBrand).length && (
          <div
            ref={parentRef}
            className={cn.brandsTiles}
            style={{ '--tile-columns': columnCount }}
          >
            <div
              className={cn.brandsVirtualList}
              style={{
                '--virtual-total-size': `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const rowIndex = virtualRow.index
                const start = rowIndex * columnCount
                const rowItems = brandsBySearch.slice(
                  start,
                  start + columnCount
                )

                return (
                  <div
                    key={virtualRow.key}
                    className={cn.brandsVirtualRow}
                    style={{ '--virtual-row-start': `${virtualRow.start}px` }}
                  >
                    {rowItems.map((brandBySearch) => (
                      <Tile
                        text={brandBySearch.description}
                        handleClickTile={() => handleClickBrand(brandBySearch)}
                        key={brandBySearch.id}
                      />
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      <Loader loaderType="modal" isModalOpen={brandsLoading} />
      {Object.keys(currentBrand).length > 0 && (
        <TreeTiles
          key={currentBrand.id}
          tree={categoriesTreeByBrand}
          treeLoading={categoriesTreeByBrandLoading}
          loadingError={categoriesTreeByBrandLoadingError}
          setTree={setCategoriesTreeByBrand}
        />
      )}
    </section>
  )
}

export default CategoriesByBrandsCatalog
